import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TreePine, Heart, Plus, Edit, Trash2, ExternalLink, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const campaignSchema = z.object({
  name: z.string().min(1, "Name ist erforderlich"),
  description: z.string().optional(),
  campaign_type: z.enum(["trees", "donation", "custom"]),
  partner_name: z.string().optional(),
  partner_url: z.string().url("Gültige URL erforderlich").optional().or(z.literal("")),
  cost_per_action: z.number().min(0, "Kosten müssen positiv sein"),
  target_amount: z.number().min(1, "Ziel muss positiv sein").optional(),
  is_active: z.boolean().default(true),
});

type CampaignForm = z.infer<typeof campaignSchema>;

interface CharityCampaign {
  id: string;
  name: string;
  description: string | null;
  campaign_type: string;
  partner_name: string | null;
  partner_url: string | null;
  cost_per_action: number;
  target_amount: number | null;
  current_amount: number;
  is_active: boolean;
  start_date: string;
  end_date: string | null;
  image_url: string | null;
  total_impact?: number;
  participating_tenants?: number;
}

export function ForestDonationModule() {
  const [campaigns, setCampaigns] = useState<CharityCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState<CharityCampaign | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CampaignForm>({
    resolver: zodResolver(campaignSchema),
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const { data: campaignsData, error } = await supabase
        .from("charity_campaigns")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Calculate additional metrics for each campaign
      const campaignsWithMetrics = await Promise.all(
        (campaignsData || []).map(async (campaign) => {
          // Get total impact from environmental_impact table
          const { data: impactData } = await supabase
            .from("environmental_impact")
            .select("trees_planted, donation_amount, tenant_id")
            .eq("campaign_id", campaign.id);

          const totalImpact = impactData?.reduce((sum, impact) => 
            sum + impact.trees_planted + impact.donation_amount, 0) || 0;
          
          const participatingTenants = new Set(impactData?.map(i => i.tenant_id) || []).size;

          return {
            ...campaign,
            total_impact: totalImpact,
            participating_tenants: participatingTenants,
          };
        })
      );

      setCampaigns(campaignsWithMetrics);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast({
        title: "Fehler",
        description: "Kampagnen konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (campaign: CharityCampaign | null = null) => {
    setEditingCampaign(campaign);
    if (campaign) {
      reset({
        name: campaign.name,
        description: campaign.description || "",
        campaign_type: campaign.campaign_type as "trees" | "donation" | "custom",
        partner_name: campaign.partner_name || "",
        partner_url: campaign.partner_url || "",
        cost_per_action: campaign.cost_per_action,
        target_amount: campaign.target_amount || undefined,
        is_active: campaign.is_active,
      });
    } else {
      reset({
        campaign_type: "trees",
        cost_per_action: 1.0,
        is_active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const onSubmit = async (data: CampaignForm) => {
    try {
      const campaignData = {
        name: data.name,
        campaign_type: data.campaign_type,
        cost_per_action: Number(data.cost_per_action),
        target_amount: data.target_amount ? Number(data.target_amount) : null,
        partner_url: data.partner_url || null,
        partner_name: data.partner_name || null,
        description: data.description || null,
        is_active: data.is_active,
      };

      if (editingCampaign) {
        const { error } = await supabase
          .from("charity_campaigns")
          .update(campaignData)
          .eq("id", editingCampaign.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("charity_campaigns")
          .insert(campaignData);
        if (error) throw error;
      }

      await fetchCampaigns();
      setIsDialogOpen(false);
      toast({
        title: "Erfolgreich",
        description: editingCampaign ? "Kampagne aktualisiert." : "Kampagne erstellt.",
      });
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast({
        title: "Fehler",
        description: "Kampagne konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  const deleteCampaign = async (campaignId: string) => {
    try {
      const { error } = await supabase
        .from("charity_campaigns")
        .delete()
        .eq("id", campaignId);
      
      if (error) throw error;
      
      await fetchCampaigns();
      toast({
        title: "Erfolgreich",
        description: "Kampagne gelöscht.",
      });
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast({
        title: "Fehler",
        description: "Kampagne konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  const getCampaignIcon = (type: string) => {
    switch (type) {
      case "trees":
        return <TreePine className="h-5 w-5 text-green-600" />;
      case "donation":
        return <Heart className="h-5 w-5 text-red-600" />;
      default:
        return <Target className="h-5 w-5 text-blue-600" />;
    }
  };

  const getCampaignTypeLabel = (type: string) => {
    const labels = {
      trees: "Baumpflanzung",
      donation: "Spendenaktion",
      custom: "Benutzerdefiniert",
    };
    return labels[type as keyof typeof labels] || type;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Wald- & Spendenfunktion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalTreesPlanted = campaigns
    .filter(c => c.campaign_type === "trees")
    .reduce((sum, c) => sum + (c.total_impact || 0), 0);

  const totalDonations = campaigns
    .filter(c => c.campaign_type === "donation")
    .reduce((sum, c) => sum + (c.total_impact || 0), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TreePine className="h-5 w-5" />
          Wald- & Spendenfunktion
        </CardTitle>
        <CardDescription>
          Verwalten Sie Umwelt- und Spendenkampagnen für die Plattform
        </CardDescription>
        <div className="flex justify-end">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openEditDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Neue Kampagne
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingCampaign ? "Kampagne bearbeiten" : "Neue Kampagne erstellen"}
                </DialogTitle>
                <DialogDescription>
                  Konfigurieren Sie eine Umwelt- oder Spendenkampagne.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Kampagnen-Name</Label>
                    <Input {...register("name")} placeholder="z.B. Herzenswald 2024" />
                    {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="campaign_type">Kampagnen-Typ</Label>
                    <select {...register("campaign_type")} className="w-full p-2 border rounded">
                      <option value="trees">Baumpflanzung</option>
                      <option value="donation">Spendenaktion</option>
                      <option value="custom">Benutzerdefiniert</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea {...register("description")} placeholder="Beschreibung der Kampagne" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="partner_name">Partner-Name</Label>
                    <Input {...register("partner_name")} placeholder="z.B. Plant-for-the-Planet" />
                  </div>
                  <div>
                    <Label htmlFor="partner_url">Partner-Website</Label>
                    <Input {...register("partner_url")} placeholder="https://example.com" />
                    {errors.partner_url && <p className="text-sm text-red-500">{errors.partner_url.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cost_per_action">Kosten pro Aktion (€)</Label>
                    <Input 
                      {...register("cost_per_action", { valueAsNumber: true })} 
                      type="number" 
                      step="0.01"
                      placeholder="1.00" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="target_amount">Ziel-Anzahl</Label>
                    <Input 
                      {...register("target_amount", { valueAsNumber: true })} 
                      type="number" 
                      placeholder="10000" 
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch {...register("is_active")} />
                  <Label>Kampagne ist aktiv</Label>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit">
                    {editingCampaign ? "Aktualisieren" : "Erstellen"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Global Impact Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <TreePine className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">Bäume gepflanzt</span>
            </div>
            <div className="text-2xl font-bold">{Math.floor(totalTreesPlanted).toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">
              Durch Empfehlungen finanziert
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500/10 to-red-600/10 rounded-lg p-4 border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-600">Spenden gesammelt</span>
            </div>
            <div className="text-2xl font-bold">{totalDonations.toFixed(2)}€</div>
            <div className="text-xs text-muted-foreground">
              Für soziale Zwecke
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500/10 to-blue-600/10 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Aktive Kampagnen</span>
            </div>
            <div className="text-2xl font-bold">{campaigns.filter(c => c.is_active).length}</div>
            <div className="text-xs text-muted-foreground">
              Laufende Projekte
            </div>
          </div>
        </div>

        {/* Campaigns Overview */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Aktive Kampagnen</h3>
          
          {campaigns.filter(c => c.is_active).map((campaign) => (
            <div key={campaign.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {getCampaignIcon(campaign.campaign_type)}
                  <div>
                    <h4 className="font-semibold">{campaign.name}</h4>
                    <p className="text-sm text-muted-foreground">{campaign.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{getCampaignTypeLabel(campaign.campaign_type)}</Badge>
                  {campaign.partner_name && campaign.partner_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(campaign.partner_url!, "_blank")}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      {campaign.partner_name}
                    </Button>
                  )}
                </div>
              </div>

              {campaign.target_amount && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Fortschritt</span>
                    <span>{campaign.current_amount} / {campaign.target_amount}</span>
                  </div>
                  <Progress 
                    value={(campaign.current_amount / campaign.target_amount) * 100} 
                    className="h-2"
                  />
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="font-medium">{campaign.total_impact || 0}</div>
                  <div className="text-muted-foreground">Gesamte Wirkung</div>
                </div>
                <div>
                  <div className="font-medium">{campaign.participating_tenants || 0}</div>
                  <div className="text-muted-foreground">Teilnehmende Unternehmen</div>
                </div>
                <div>
                  <div className="font-medium">{campaign.cost_per_action}€</div>
                  <div className="text-muted-foreground">Pro Empfehlung</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* All Campaigns Table */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Alle Kampagnen</h3>
          
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kampagne</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Fortschritt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{campaign.name}</div>
                        <div className="text-sm text-muted-foreground">{campaign.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCampaignIcon(campaign.campaign_type)}
                        <span>{getCampaignTypeLabel(campaign.campaign_type)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {campaign.partner_name || "Kein Partner"}
                    </TableCell>
                    <TableCell>
                      {campaign.target_amount ? (
                        <div className="space-y-1">
                          <div className="text-sm">{campaign.current_amount} / {campaign.target_amount}</div>
                          <Progress 
                            value={(campaign.current_amount / campaign.target_amount) * 100} 
                            className="h-1"
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Kein Ziel</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {campaign.is_active ? (
                        <Badge variant="default">Aktiv</Badge>
                      ) : (
                        <Badge variant="secondary">Inaktiv</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openEditDialog(campaign)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => deleteCampaign(campaign.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}